import IItem from '../../types/product_delivery';
import {ProductNote} from '../../types/DocumentsTypes/Document';
import type {AiMatchingProductMemory} from '../../api/customer/ai_matching.api';
import {AiMatchedLine, ExtractedProductLine, MatchedProductLine, ProductMatchCandidate} from './types';

/** לפי שדה `available` מהמחירון — 0 = לא זמין; undefined נחשב זמין (תאימות לאחור) */
export const isCatalogProductAvailable = (product: IItem): boolean => {
    const a = product.available;
    if (a === undefined || a === null) return true;
    return Number(a) !== 0;
};

export const normalizeProductMemoryText = (value?: string | null) =>
    (value ?? '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\"'׳״.,;:()[\]{}]/g, ' ')
        .replace(/\s+/g, ' ');

const bigrams = (value: string) => {
    const normalized = normalizeProductMemoryText(value).replace(/\s/g, '');
    if (normalized.length <= 1) return normalized ? [normalized] : [];
    const grams: string[] = [];
    for (let i = 0; i < normalized.length - 1; i += 1) {
        grams.push(normalized.slice(i, i + 2));
    }
    return grams;
};

const diceScore = (a: string, b: string) => {
    const left = bigrams(a);
    const right = bigrams(b);
    if (!left.length || !right.length) return 0;
    const used = new Set<number>();
    let matches = 0;
    left.forEach((gram) => {
        const index = right.findIndex((candidate, idx) => candidate === gram && !used.has(idx));
        if (index >= 0) {
            used.add(index);
            matches += 1;
        }
    });
    return (2 * matches) / (left.length + right.length);
};

export const localProductScore = (query: string, product: IItem) => {
    const q = normalizeProductMemoryText(query);
    const name = normalizeProductMemoryText(product.name);
    const barcode = normalizeProductMemoryText(product.barcode);
    const externalId = normalizeProductMemoryText(product.external_id);
    const deprioritizeUnavailable = (score: number) =>
        (isCatalogProductAvailable(product) ? score : Math.max(0, score - 0.12));

    if (!q || !name) return 0;
    if (q === name || q === barcode || q === externalId) return deprioritizeUnavailable(1);
    if (name.includes(q) || q.includes(name)) return deprioritizeUnavailable(0.92);

    const fuzzy = diceScore(q, name);
    const priorityBoost = Math.min(Number(product.priority ?? 0), 100) / 1000;
    return deprioritizeUnavailable(Math.min(1, fuzzy + priorityBoost));
};

export const buildProductCandidates = (
    extractedLine: ExtractedProductLine,
    products: IItem[],
    fuzzyLimit = 4,
    previousProducts: any[] = [],
    excludeUnavailable = false,
): ProductMatchCandidate[] => {
    const catalog = excludeUnavailable
        ? products.filter((product) => isCatalogProductAvailable(product))
        : products;
    const previousOrdersByProductId = new Map<number, ProductMatchCandidate['previousOrders']>();
    previousProducts.forEach((doc) => {
        (doc?.products ?? []).forEach((previousProduct: any) => {
            const productId = Number(previousProduct.id);
            if (!productId) return;
            const usages = previousOrdersByProductId.get(productId) ?? [];
            usages.push({
                documentId: doc.id,
                date: doc.date,
                type: doc.type,
                amount: Number(previousProduct.amount ?? 0),
                secondAmount: Number(previousProduct.secondAmount ?? 0),
                usedAmount: Number(previousProduct.usedAmount ?? previousProduct.amount ?? 0),
                usedUnit: previousProduct.usedUnit ?? previousProduct.unit,
                usedUnitIndex: previousProduct.usedUnitIndex ?? previousProduct.unitIndex,
            });
            previousOrdersByProductId.set(productId, usages);
        });
    });

    const catalogStockRank = (c: ProductMatchCandidate) => (c.catalogAvailable === false ? 0 : 1);

    const toCandidate = (product: IItem, score: number): ProductMatchCandidate => {
        const productId = Number(product.id);
        const previousOrders = previousOrdersByProductId.get(productId);
        return {
            productId,
            name: product.name,
            unit: product.unit,
            availableUnits: product.available_units,
            catalogAvailable: isCatalogProductAvailable(product),
            price: product.totalPrice,
            barcode: product.barcode,
            externalId: product.external_id,
            conversionRatio: product.conversion_ratio,
            unitPreference: product.unit_preferece,
            priority: Number(product.priority ?? 0),
            prevOrdered: previousOrders?.length ? 1 : 0,
            previousOrders,
            score: Number(score.toFixed(3)),
        };
    };

    const candidatesByProductId = new Map<number, ProductMatchCandidate>();
    const addCandidate = (candidate: ProductMatchCandidate) => {
        const existing = candidatesByProductId.get(candidate.productId);
        const bestIdentity = !existing || candidate.score >= existing.score ? candidate : existing;
        candidatesByProductId.set(candidate.productId, {
            ...bestIdentity,
            score: Math.max(existing?.score ?? 0, candidate.score),
            priority: Math.max(Number(existing?.priority ?? 0), Number(candidate.priority ?? 0)),
            prevOrdered: existing?.prevOrdered === 1 || candidate.prevOrdered === 1 ? 1 : 0,
            previousOrders: existing?.previousOrders ?? candidate.previousOrders,
            catalogAvailable: candidate.catalogAvailable !== false && existing?.catalogAvailable !== false,
        });
    };

    catalog
        .map((product) => ({
            product,
            score: localProductScore(extractedLine.productName, product),
        }))
        .sort((a, b) =>
            (b.score - a.score)
            || (Number(isCatalogProductAvailable(b.product)) - Number(isCatalogProductAvailable(a.product)))
        )
        .slice(0, fuzzyLimit)
        .forEach(({product, score}) => addCandidate(toCandidate(product, score)));

    catalog
        .filter((product) => Number(product.priority ?? 0) > 0)
        .forEach((product) => addCandidate(toCandidate(product, localProductScore(extractedLine.productName, product))));

    catalog
        .filter((product) => previousOrdersByProductId.has(Number(product.id)))
        .forEach((product) => addCandidate(toCandidate(product, localProductScore(extractedLine.productName, product))));

    return Array.from(candidatesByProductId.values())
        .sort((a, b) =>
            (b.score - a.score) ||
            (Number(b.prevOrdered ?? 0) - Number(a.prevOrdered ?? 0)) ||
            (Number(b.priority ?? 0) - Number(a.priority ?? 0)) ||
            (catalogStockRank(b) - catalogStockRank(a)) ||
            a.name.localeCompare(b.name)
        );
};

export const summarizePreviousDocumentProducts = (documents: ProductNote[], limit = 3) => {
    return documents
        .filter((doc) => Array.isArray(doc.products) && doc.products.length > 0)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, limit)
        .map((doc) => ({
            id: doc.id,
            date: doc.date,
            type: doc.type,
            products: doc.products
                .map((product) => {
                    const amount = Number(product.quantity ?? 0);
                    const secondAmount = Number(product.quantitySecond ?? 0);
                    const availableUnits = product.available_units ?? [product.unit].filter(Boolean);
                    const unitIndex = secondAmount > 0 ? 1 : 0;
                    return {
                        id: product.id,
                        name: product.name,
                        amount,
                        secondAmount,
                        usedAmount: secondAmount > 0 ? secondAmount : amount,
                        unit: product.unit,
                        availableUnits,
                        unitIndex,
                        usedUnitIndex: unitIndex,
                        usedUnit: availableUnits[unitIndex] ?? product.unit,
                        price: product.totalPrice,
                    };
                })
                .filter((product) => product.amount > 0 || product.secondAmount > 0),
        }));
};

const findPreviousUnitIndex = (previousProducts: any[], productId: number) => {
    for (const doc of previousProducts ?? []) {
        const previousProduct = doc?.products?.find((product: any) => Number(product.id) === Number(productId));
        if (!previousProduct) continue;
        if (previousProduct.usedUnitIndex !== undefined) return Number(previousProduct.usedUnitIndex);
        if (previousProduct.unitIndex !== undefined) return Number(previousProduct.unitIndex);
        if ((previousProduct.secondAmount ?? 0) > 0) return 1;
        if ((previousProduct.amount ?? 0) > 0) return 0;
    }
    return undefined;
};

const inferUnitIndex = ({
    unitText,
    product,
    productId,
    previousProducts,
    conversionRateBlocker,
}: {
    unitText?: string | null;
    product?: IItem;
    productId: number | null;
    previousProducts?: any[];
    conversionRateBlocker?: number;
}) => {
    if (!product) return 0;

    const normalizedUnit = normalizeProductMemoryText(unitText);
    if (normalizedUnit) {
        const explicitUnitIndex = product.available_units?.findIndex((unit) => normalizeProductMemoryText(unit) === normalizedUnit) ?? -1;
        if (explicitUnitIndex >= 0) return explicitUnitIndex;
    }

    if (productId !== null) {
        const pref = Number(product.unit_preferece ?? 0);
        if ((pref === 1 || pref === 2) && (product.available_units?.[pref - 1])) {
            return pref - 1;
        }
    }

    if ((conversionRateBlocker === 1 || conversionRateBlocker === 2) && (product.available_units?.length ?? 0) > 1) {
        return 1;
    }

    if (productId !== null) {
        const previousUnitIndex = findPreviousUnitIndex(previousProducts ?? [], productId);
        if (previousUnitIndex !== undefined) return previousUnitIndex;
    }

    return 0;
};

const findUnitIndexByText = (product: IItem, unitText?: string | null) => {
    const normalizedUnit = normalizeProductMemoryText(unitText);
    if (!normalizedUnit) return undefined;
    const index = product.available_units?.findIndex((unit) => normalizeProductMemoryText(unit) === normalizedUnit) ?? -1;
    return index >= 0 ? index : undefined;
};

const productToCandidate = (product: IItem, score = 1): ProductMatchCandidate => ({
    productId: Number(product.id),
    name: product.name,
    unit: product.unit,
    availableUnits: product.available_units,
    catalogAvailable: isCatalogProductAvailable(product),
    price: product.totalPrice,
    barcode: product.barcode,
    externalId: product.external_id,
    conversionRatio: product.conversion_ratio,
    unitPreference: product.unit_preferece,
    priority: product.priority,
    prevOrdered: 0,
    score,
});

const memoryMatchScore = (line: ExtractedProductLine, memory: AiMatchingProductMemory) => {
    const lineName = normalizeProductMemoryText(line.productName);
    const memoryName = normalizeProductMemoryText(memory.req_name);
    const lineUnit = normalizeProductMemoryText(line.unit);
    const memoryUnit = normalizeProductMemoryText(memory.req_unit);
    if (!lineName || !memoryName) return 0;
    if (lineUnit && memoryUnit && lineUnit !== memoryUnit) return 0;
    if (lineName === memoryName) return 1;
    return diceScore(lineName, memoryName);
};

const isPerfectProductCandidateMatch = (line: ExtractedProductLine, candidate: ProductMatchCandidate) => {
    const query = normalizeProductMemoryText(line.productName);
    if (!query) return false;
    return [
        candidate.name,
        candidate.barcode,
        candidate.externalId,
    ].some((value) => normalizeProductMemoryText(value) === query);
};

const applyMemoryAmountTransform = (amount: number, metadata?: Record<string, unknown> | null) => {
    const mode = String(metadata?.amount_mode ?? 'as_is');
    const factor = Number(metadata?.amount_factor ?? 1);
    if (!Number.isFinite(factor) || factor <= 0) return amount;
    if (mode === 'multiply') return amount * factor;
    if (mode === 'divide') return amount / factor;
    return amount;
};

export const applySavedProductMemoryMatches = ({
    extractedLines,
    products,
    candidatesByLine,
    savedMatches,
    previousProducts = [],
    conversionRateBlocker = 0,
}: {
    extractedLines: ExtractedProductLine[];
    products: IItem[];
    candidatesByLine: Record<string, ProductMatchCandidate[]>;
    savedMatches: AiMatchingProductMemory[];
    previousProducts?: any[];
    conversionRateBlocker?: number;
}) => {
    const productMap = new Map(products.map((product) => [Number(product.id), product]));
    const memoryByExactKey = new Map<string, AiMatchingProductMemory>();
    const memoryByName = new Map<string, AiMatchingProductMemory>();

    savedMatches.forEach((memory) => {
        const name = normalizeProductMemoryText(memory.req_name);
        const unit = normalizeProductMemoryText(memory.req_unit);
        if (!name) return;
        memoryByExactKey.set(`${name}::${unit}`, memory);
        if (!memoryByName.has(name)) memoryByName.set(name, memory);
    });

    const matchedLines: MatchedProductLine[] = [];
    const unresolvedLines: ExtractedProductLine[] = [];

    extractedLines.forEach((line) => {
        const name = normalizeProductMemoryText(line.productName);
        const unit = normalizeProductMemoryText(line.unit);
        const exactMemoryMatch = memoryByExactKey.get(`${name}::${unit}`) ?? memoryByName.get(name);
        const fuzzyPerfectMemoryMatch = savedMatches
            .map((memory) => ({memory, score: memoryMatchScore(line, memory)}))
            .filter(({score}) => score >= 1)
            .sort((a, b) => {
                const aUnitExact = normalizeProductMemoryText(a.memory.req_unit) === unit ? 1 : 0;
                const bUnitExact = normalizeProductMemoryText(b.memory.req_unit) === unit ? 1 : 0;
                return bUnitExact - aUnitExact;
            })[0]?.memory;
        const memoryMatch = exactMemoryMatch ?? fuzzyPerfectMemoryMatch;
        const product = memoryMatch ? productMap.get(Number(memoryMatch.match_id)) : undefined;

        if (memoryMatch && product) {
            const metadata = memoryMatch.metadata ?? null;
            const unitIndex = findUnitIndexByText(product, memoryMatch.match_unit) ?? inferUnitIndex({
                unitText: line.unit,
                product,
                productId: Number(product.id),
                previousProducts,
                conversionRateBlocker,
            });
            const rememberedUnitIndex = Number(metadata?.unit_index);
            const resolvedUnitIndex = Number.isFinite(rememberedUnitIndex) && product.available_units?.[rememberedUnitIndex]
                ? rememberedUnitIndex
                : unitIndex;
            const existingCandidate = (candidatesByLine[line.lineId] ?? []).find((candidate) => Number(candidate.productId) === Number(product.id));
            const memoryCandidate = {
                ...(existingCandidate ?? productToCandidate(product, 1)),
                score: 1,
            };
            const otherCandidates = (candidatesByLine[line.lineId] ?? [])
                .filter((candidate) => Number(candidate.productId) !== Number(product.id));

            matchedLines.push({
                lineId: line.lineId,
                extracted: line,
                candidates: [memoryCandidate, ...otherCandidates],
                matchedProduct: product,
                amount: applyMemoryAmountTransform(Number(line.amount ?? 0), metadata),
                unitIndex: resolvedUnitIndex,
                price: Number(line.price ?? product.totalPrice ?? 0),
                confidence: 0.99,
                confirmed: true,
                reason: 'התאמה מלאה מזיכרון התאמות קודם ללקוח/ספק',
                memoryMetadata: metadata ?? undefined,
            });
            return;
        }

        const exactCandidate = (candidatesByLine[line.lineId] ?? []).find((candidate) => isPerfectProductCandidateMatch(line, candidate));
        const exactProduct = exactCandidate ? productMap.get(Number(exactCandidate.productId)) : undefined;
        if (exactProduct) {
            const unitIndex = inferUnitIndex({
                unitText: line.unit,
                product: exactProduct,
                productId: Number(exactProduct.id),
                previousProducts,
                conversionRateBlocker,
            });
            matchedLines.push({
                lineId: line.lineId,
                extracted: line,
                candidates: candidatesByLine[line.lineId] ?? [],
                matchedProduct: exactProduct,
                amount: Number(line.amount ?? 0),
                unitIndex,
                price: Number(line.price ?? exactProduct.totalPrice ?? 0),
                confidence: 0.98,
                confirmed: true,
                reason: 'התאמה מדויקת לשם מוצר במערכת',
            });
            return;
        }

        unresolvedLines.push(line);
    });

    return {matchedLines, unresolvedLines};
};

export const applyAiMatches = (
    extractedLines: ExtractedProductLine[],
    products: IItem[],
    candidatesByLine: Record<string, ProductMatchCandidate[]>,
    aiMatches: AiMatchedLine[],
    previousProducts: any[] = [],
    conversionRateBlocker = 0
): MatchedProductLine[] => {
    const productMap = new Map(products.map((product) => [Number(product.id), product]));
    const aiByLine = new Map(aiMatches.map((match) => [match.lineId, match]));

    return extractedLines.map((line) => {
        const aiMatch = aiByLine.get(line.lineId);
        const fallbackCandidate = candidatesByLine[line.lineId]?.[0];

        // Respect explicit AI null — AI declared "no candidate fits".
        // Only fall back to the best fuzzy candidate when the AI didn't return productId at all (undefined).
        const aiSaidNoMatch = aiMatch !== undefined && aiMatch.productId === null;
        const aiPickedProductId = typeof aiMatch?.productId === 'number' ? aiMatch.productId : undefined;
        const productId: number | null = aiSaidNoMatch
            ? null
            : aiPickedProductId !== undefined
                ? aiPickedProductId
                : fallbackCandidate?.productId ?? null;

        const product = productId ? productMap.get(Number(productId)) : undefined;
        const unitIndex = inferUnitIndex({
            // Use only the extracted source unit here. If the source had no unit, the AI match
            // must not invent one over client history or conversion_rate_blocker defaults.
            unitText: line.unit,
            product,
            productId,
            previousProducts,
            conversionRateBlocker,
        });

        // Confidence: when AI explicitly said null we keep its (low) confidence — UI will mark as unconfirmed.
        const baseConfidence = aiSaidNoMatch
            ? aiMatch.confidence
            : aiMatch?.confidence ?? fallbackCandidate?.score ?? line.confidence ?? 0;
        const confidence = Math.max(0, Math.min(1, Number(baseConfidence)));

        const reason = aiMatch?.reason ?? (aiSaidNoMatch ? 'AI לא מצא מוצר תואם — נדרש בחירה ידנית' : undefined);

        return {
            lineId: line.lineId,
            extracted: line,
            candidates: candidatesByLine[line.lineId] ?? [],
            matchedProduct: product,
            amount: Number(aiMatch?.amount ?? line.amount ?? 0),
            unitIndex,
            price: Number(aiMatch?.price ?? line.price ?? product?.totalPrice ?? 0),
            confidence,
            confirmed: !aiSaidNoMatch && Boolean(product) && confidence >= 0.75,
            reason,
        };
    });
};

export const matchedLinesToItems = (
    lines: MatchedProductLine[],
    options: {includeSourceNotes?: boolean} = {},
): IItem[] => {
    const includeSourceNotes = options.includeSourceNotes !== false; // default: true (keep legacy behavior)
    return lines
        .filter((line) => line.matchedProduct && line.amount > 0)
        .map((line, index) => {
            const base = line.matchedProduct!;
            const usesSecondUnit = line.unitIndex > 0 && (base.available_units?.length ?? 0) > 1;
            const sourceText = includeSourceNotes
                ? (line.extracted.rawText || [
                    line.extracted.amount,
                    line.extracted.unit,
                    line.extracted.productName,
                ].filter(Boolean).join(' ')).trim()
                : '';
            return {
                ...base,
                quantity: usesSecondUnit ? 0 : line.amount,
                quantitySecond: usesSecondUnit ? line.amount : 0,
                quantityReturns: 0,
                quantitySecondReturns: 0,
                totalPrice: line.price ?? base.totalPrice,
                priceTouched: line.price !== undefined && line.price !== base.totalPrice,
                notePlacement: index,
                notes: sourceText,
                conversion_ratio: usesSecondUnit ? (base.conversion_ratio ?? 0) : base.conversion_ratio,
            };
        });
};

